import { Component, inject } from '@angular/core';
import { ButtonsModule, ComponentsModule, DialogModule, DialogService, FormFieldModule, IconModule, PanelModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { ReactiveFormsModule } from '@angular/forms';
import { RegionService } from '@lib/region/region.service';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { Priority, PrioritySingleResponse, StorePriorityRequest, createPriorityFormGroup } from '@lib/api/models/priorities';

export interface RegionData {
    id: string;
    name: string;
}

export interface Data {
    region: RegionData;
    priority: Priority;
}

@Component({
    selector: 'app-edit-region-priority-dialog',
    imports: [
        ButtonsModule,
        DialogModule,
        ErrorPanelComponent,
        FormFieldModule,
        ReactiveFormsModule,
        IconModule,
        PanelModule,
        ComponentsModule
    ],
    templateUrl: './edit-region-priority-dialog.component.html',
    standalone: true,
    styleUrl: './edit-region-priority-dialog.component.scss'
})

export class EditRegionPriorityDialogComponent {

    private readonly _regionService = inject(RegionService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly data = inject<Data>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly priorityFormGroup = createPriorityFormGroup(this.data.priority);

    editPriorityRequest = new SendableRequest<PrioritySingleResponse, StorePriorityRequest>({
        request: payload => this._regionService.updatePriority(this.data.priority.id, payload!),

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'Priority edited successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            this.dialogRef.close();
        }
    });

    edit() {

        if (this.priorityFormGroup.invalid) {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Please fill in all required fields',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
            return;
        }

        const priorityData = {
            'id': this.data.priority.id,
            'name': this.priorityFormGroup.value.name ?? '',
            'description': this.priorityFormGroup.value.description ?? '',
            'delay': this.priorityFormGroup.value.delay ?? 0,
            'status': this.data.priority.status ?? true
        };

        this.editPriorityRequest.send(priorityData);
    }

    public static show(dialog: DialogService, data: Data) {
        return dialog.openDialog<DialogRef, Data>(EditRegionPriorityDialogComponent, {
            data
        });
    }

}
